import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Modal, Spin} from 'antd';
import {useProject} from '../../../providers/ClientProjectProvider';
import ProjectFormProvider from '../../../providers/ProjectFormProvider';
import TalentPoolProvider from '../../../providers/TalentPoolProvider';
import {ProjectVisibilitySettings} from '../../projects/ProjectVisibilitySettingsInfo';
import LoadingSpinner from "../../../components/LoadingSpinner";

const QuickHireOpenToTalentModal = ({isOpen, onClose, message, onSuccess, onShowTalent, initialState = 'confirm'}) => {
    const {postUpdate: postUpdateProject} = useProject();
    const [modalState, setModalState] = useState(initialState);

    return (
        <Modal
            open={isOpen}
            footer={null}
            title={null}
            closable={modalState !== 'loading'}
            maskClosable={false}
            destroyOnClose={true}
            style={{top: 50}}
            width={750}
            onCancel={onClose}
            onClose={onClose}
            afterClose={() => setModalState('confirm')}
        >
            {modalState === 'confirm' && (
                <div className="flex flex-col gap-6 py-4 px-2">
                    <div className="flex flex-col gap-3">
                        <p className="text-xl font-bold text-gray-800 mb-0">
                            {message}
                        </p>
                        <p className="text-base text-gray-600 mb-0">
                            Would you like to open this project to all talent matching the requested roles? This will allow qualified candidates to discover and submit proposals.
                        </p>
                        <p className="text-sm text-gray-400 mb-0">
                            Please note: this action cannot be undone.
                        </p>
                    </div>
                    <div className="flex flex-row gap-3 justify-end">
                        <Button onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={() => setModalState('form')}>
                            Yes, Open to Matching Talent
                        </Button>
                    </div>
                </div>
            )}

            {modalState === 'loading' && (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <LoadingSpinner size="lg"/>
                    <p className="text-base text-gray-500 mb-0">Updating your project, please wait...</p>
                </div>
            )}

            {modalState === 'success' && (
                <div className="flex flex-col gap-6 py-4 px-2">
                    <div className="flex flex-col gap-3">
                        <p className="text-xl font-bold text-gray-800 mb-0">
                            Your project is now open to matching talent.
                        </p>
                        <p className="text-base text-gray-600 mb-0">
                            Qualified candidates will be able to discover this project and submit proposals based on your visibility settings.
                        </p>
                    </div>
                    <div className="flex flex-row gap-3 justify-end">
                        <Button onClick={onClose}>
                            Dismiss
                        </Button>
                        {onShowTalent && (
                            <Button type="primary" onClick={() => {
                                onClose();
                                onShowTalent();
                            }}>
                                Show Matching Talent
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {modalState === 'error' && (
                <div className="flex flex-col gap-6 py-4 px-2">
                    <div className="flex flex-col gap-3">
                        <p className="text-xl font-bold text-gray-800 mb-0">
                            We were unable to update your project.
                        </p>
                        <p className="text-base text-gray-600 mb-0">
                            An unexpected error occurred. Please try again later or contact support if the issue persists.
                        </p>
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={onClose}>Close</Button>
                    </div>
                </div>
            )}

            {modalState === 'form' && (
                <ProjectFormProvider>
                    <TalentPoolProvider>
                        <ProjectVisibilitySettings
                            onSubmit={async (visibilityProps) => {
                                setModalState('loading');
                                try {
                                    let updatedProject = await postUpdateProject({is_direct_hire: false, ...visibilityProps});
                                    if (updatedProject && updatedProject.isHiringSourceQuickHireOpenToTalent()) {
                                        onSuccess?.();
                                        setModalState('success');
                                    } else {
                                        setModalState('error');
                                    }
                                } catch (e) {
                                    setModalState('error');
                                }
                            }}
                            onClose={onClose}
                            showActions={true}
                            formOnly={true}
                            skipWarnings={true}
                            instructions={(
                                <div className="flex flex-col gap-1 mb-4 pb-2 border-b border-gray-200">
                                    <p className="text-xl font-bold text-gray-800 mb-1">
                                        Review your visibility settings before going live.
                                    </p>
                                    <p className="text-sm text-gray-500 mb-0">
                                        These settings determine which talent can discover and submit proposals
                                        for your project. Once confirmed, click <strong>Update Settings</strong> to open the project.
                                    </p>
                                </div>
                            )}
                        />
                    </TalentPoolProvider>
                </ProjectFormProvider>
            )}
        </Modal>
    );
};

QuickHireOpenToTalentModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    message: PropTypes.string.isRequired,
    onSuccess: PropTypes.func,
    onShowTalent: PropTypes.func,
};

export default QuickHireOpenToTalentModal;